/**
 * What the shared RBAC engine hands back, and the two things every part of this
 * screen needs to say about a role. React-free on purpose: the naming and the
 * column track are read by the list, the row and the rights card alike, and none
 * of them should have to import a component to get at them.
 */
import type { StatusTone } from "../../../ui";

export interface Permission {
  key: string;
  category: string;
  scope: "site" | "platform";
}

export interface RoleRow {
  id: string;
  key: string;
  name: Record<string, string>;
  isSystem: boolean;
  isSuperadmin: boolean;
  permissionCount: number | null;
  memberCount: number;
}

export interface RoleDetail {
  id: string;
  key: string;
  name: Record<string, string>;
  isSystem: boolean;
  isSuperadmin: boolean;
  permissionKeys: string[];
  inheritedPermissionKeys: string[];
}

/**
 * Role, what kind it is, how many people hold it, how much of the CRM it opens.
 *
 * One constant, read by the column head and by every row - a head and a row
 * with different track counts do not fail loudly, the cells just quietly stop
 * lining up with their own labels.
 */
export const ROLE_COLS = "minmax(0,2fr) 1fr .8fr .8fr 44px";

/** `manage:crm.sales` -> `manage_crm.sales`, which is how the label keys are
 *  spelled (a colon cannot be a JSON path segment here). */
export const permSlug = (key: string) => key.replace(/:/g, "_");

export function roleLabel(r: { name: Record<string, string>; key: string }, locale: string): string {
  return r.name?.[locale] ?? r.name?.en ?? r.key;
}

/** Locked, built in, or somebody's own - the one status a role has. */
export function roleKind(r: { isSuperadmin: boolean; isSystem: boolean }): { tone: StatusTone; key: string } {
  if (r.isSuperadmin) return { tone: "info", key: "roles.superadminBadge" };
  if (r.isSystem) return { tone: "mut", key: "roles.systemBadge" };
  return { tone: "ok", key: "roles.customBadge" };
}
